import { useEffect, useState } from "react";
import {
  AlertCircle,
  Bell,
  CheckCheck,
  Loader2,
  RefreshCw,
} from "lucide-react";
import {
  listNotifications,
  markNotificationRead,
  setComplianceContext,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type { NotificationItem } from "../../types/compliance";

const TYPE_META: Record<
  string,
  { label: string; color: string; dot: string }
> = {
  DUPLICATE_INVOICE: {
    label: "Duplicate Invoice",
    color: "bg-red-100 text-red-800 border-red-200",
    dot: "bg-red-500",
  },
  VAT_FAILURE: {
    label: "VAT Failure",
    color: "bg-amber-100 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
  },
  WORKFLOW_ESCALATION: {
    label: "Workflow Escalation",
    color: "bg-orange-100 text-orange-800 border-orange-200",
    dot: "bg-orange-500",
  },
  COMPLIANCE_RISK: {
    label: "Compliance Risk",
    color: "bg-blue-100 text-blue-800 border-blue-200",
    dot: "bg-blue-500",
  },
};

function typeStyle(type: string) {
  return (
    TYPE_META[type] ?? {
      label: type.replace(/_/g, " "),
      color: "bg-gray-100 text-gray-700 border-gray-200",
      dot: "bg-gray-400",
    }
  );
}

export default function NotificationsPage() {
  const { credentials } = useComplianceAuth();

  setComplianceContext(
    credentials?.tenantId ?? "default",
    credentials?.role ?? "COMPLIANCE_ADMIN",
  );

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [markingId, setMarkingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await listNotifications({
        unread_only: filter === "unread",
      });
      setItems(res.items);
      setUnreadCount(res.unread_count);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load notifications",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [filter]);

  async function handleMarkRead(notifId: string) {
    setMarkingId(notifId);
    try {
      const updated = await markNotificationRead(notifId);
      setItems((prev) =>
        prev.map((n) =>
          n.notification_id === updated.notification_id ? updated : n,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // silently ignore
    } finally {
      setMarkingId(null);
    }
  }

  async function markAllRead() {
    const unread = items.filter((n) => n.status === "pending");
    await Promise.all(unread.map((n) => markNotificationRead(n.notification_id)));
    setItems((prev) => prev.map((n) => ({ ...n, status: "read" as const })));
    setUnreadCount(0);
  }

  const displayed = items;

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Bell size={24} className="text-yellow-500" />
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => void markAllRead()}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
          )}
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        {(["all", "unread"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
              filter === f
                ? "bg-white shadow text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {f === "all" ? "All" : `Unread${unreadCount > 0 ? ` (${unreadCount})` : ""}`}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg mb-4">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {loading && displayed.length === 0 ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-gray-400" />
        </div>
      ) : displayed.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-gray-400">
          <Bell size={40} className="mb-3 opacity-30" />
          <p className="text-sm">
            {filter === "unread"
              ? "No unread notifications."
              : "No notifications yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {displayed.map((n) => {
            const meta = typeStyle(n.notification_type);
            const isUnread = n.status === "pending";
            return (
              <div
                key={n.notification_id}
                className={`flex items-start gap-3 p-4 rounded-lg border transition ${
                  isUnread
                    ? "bg-white border-gray-200 shadow-sm"
                    : "bg-gray-50 border-gray-100 opacity-70"
                }`}
              >
                {/* Type dot */}
                <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.color}`}
                    >
                      {meta.label}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(n.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs font-mono text-gray-500">
                    workflow: {n.workflow_id}
                  </p>
                  {Object.keys(n.payload).length > 0 && (
                    <p className="text-xs text-gray-600 mt-1">
                      {Object.entries(n.payload)
                        .map(([k, v]) => `${k}: ${String(v)}`)
                        .join(" · ")}
                    </p>
                  )}
                </div>

                {isUnread && (
                  <button
                    type="button"
                    onClick={() => void handleMarkRead(n.notification_id)}
                    disabled={markingId === n.notification_id}
                    title="Mark as read"
                    className="shrink-0 text-gray-400 hover:text-gray-700 transition"
                  >
                    {markingId === n.notification_id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <CheckCheck size={14} />
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
